import { GetScenarioValidationRulesResponse } from "@mutiny-group/sbo-openapi-client";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { components } from "@/@types/api";
import { INSIGHTS_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { axiosApi } from "@/Utils";
import { parseDate } from "@/Utils/parseDate";

import { useScenarioBuilderClient } from "../../api/hooks/useScenarioBuilderClient";
import { ScenarioDateRange } from "../pages/scenario-form/types";
import { getDateRangesV2 } from "./v2/getDateRanges";

type DateRangeDTO = components["schemas"]["GetDateRangesResponse"];

export const getDateRanges = (geography?: string): Promise<DateRangeDTO> => {
  return axiosApi.get("/api/v2/scenarios-new/date-ranges", {
    baseURL: INSIGHTS_BACKEND_URL,
    params: geography ? { geography } : undefined,
  });
};

type QueryFnType = typeof getDateRanges | typeof getDateRangesV2;

type UseHistoricalSpendsOptions = {
  config?: QueryConfig<QueryFnType>;
  geography?: string;
  isScenariosApiEnabled?: boolean;
};

export const useDateRanges = ({
  config,
  geography,
  isScenariosApiEnabled = false,
}: UseHistoricalSpendsOptions) => {
  const scenarioBuilderClient = useScenarioBuilderClient();
  return useQuery({
    ...config,
    queryKey: [`date_ranges`, geography],
    queryFn: isScenariosApiEnabled
      ? () => getDateRangesV2({ scenarioBuilderClient, geography })
      : () => getDateRanges(geography),
    select: (data) => {
      if (isScenariosApiEnabled) {
        const dateRangesV2Response = data as GetScenarioValidationRulesResponse;
        return {
          endDate: dayjs(dateRangesV2Response.maxScenarioEndDate),
          startDate: dayjs(dateRangesV2Response.minScenarioStartDate),
        };
      }
      return selectDateRanges(data as DateRangeDTO);
    },
  });
};

const selectDateRanges = (dateRanges: DateRangeDTO): ScenarioDateRange => {
  return {
    startDate: parseDate(dateRanges.startDate),
    endDate: parseDate(dateRanges.endDate),
  };
};
